import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { AnimatePresence, motion } from 'framer-motion';

import {
  Box,
  Center,
  FormControl,
  FormLabel,
  FormHelperText,
  HStack,
  Radio,
  RadioGroup,
  Button,
  Card,
  Text,
  VStack,
  IconButton,
  useToast,
  Checkbox,
  Input,
} from '@chakra-ui/react';
import {
  ArrowForwardIcon,
  ArrowBackIcon,
  AddIcon,
  DeleteIcon,
} from '@chakra-ui/icons';
import {
  Car,
  Bike,
  Footprints,
  Navigation,
  Route,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import LocationAutocomplete from './LocationAutocomplete';

import './RouteForm.scss';

// Format a Date as the local "YYYY-MM-DDTHH:mm" string expected by
// <input type="datetime-local"> (which works in local time, no timezone).
function toDateTimeLocalValue(date) {
  const d = date instanceof Date && !isNaN(date) ? date : new Date();
  const pad = n => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// The weather forecast only spans about two days ahead, so the departure time
// is constrained to [today, now + 2 days].
const FORECAST_WINDOW_DAYS = 2;

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export default function RouteForm({
  TravelMode,
  directionsCallback,
  handleSubmit,
  onTravelModeChange,
  isVisible,
  alreadyCalculated,
  errorMessage,
  handleDeleteWaypointFromDirectionsService,
  setDirectionsServiceOptions,
  directionsServiceOptions,
  setAlreadyCalculated,
  SearchBox,
  onLocationSelect,
  departureTime,
  setDepartureTime,
  hasWarningBanner,
  ...props
}) {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const toast = useToast();

  const handleOptionChange = option => {
    setAlreadyCalculated(false);
    let updatedOptions = [...selectedOptions];

    if (updatedOptions.includes(option)) {
      updatedOptions = updatedOptions.filter(item => item !== option);
    } else {
      updatedOptions = [...updatedOptions, option];
    }

    // Update directionsServiceOptions with the selected options
    const updatedDirectionsServiceOptions = {
      ...directionsServiceOptions,
      avoidFerries: updatedOptions.includes('avoidFerries'),
      avoidHighways: updatedOptions.includes('avoidHighways'),
      avoidTolls: updatedOptions.includes('avoidTolls'),
    };

    setSelectedOptions(updatedOptions);
    setDirectionsServiceOptions(updatedDirectionsServiceOptions);
  };

  const onFormInputTextChange = e => {
    setAlreadyCalculated(false);
    if (e.preventDefault) {
      e.preventDefault();
    }

    if (e.target.name.includes('waypoint')) {
      setDirectionsServiceOptions({
        ...directionsServiceOptions,
        waypoints: {
          ...directionsServiceOptions.waypoints,
          [e.target.name]: e.target.value,
        },
      });
    } else {
      setDirectionsServiceOptions({
        ...directionsServiceOptions,
        [e.target.name]: e.target.value,
      });
    }
  };

  const onFormWaypointTextChange = (e, waypointId) => {
    setAlreadyCalculated(false);

    const updatedWaypoints = directionsServiceOptions.waypoints.map(
      waypoint => {
        if (waypoint.id === waypointId) {
          return { ...waypoint, value: e.target.value };
        }
        return waypoint;
      }
    );

    setDirectionsServiceOptions({
      ...directionsServiceOptions,
      waypoints: updatedWaypoints,
    });
  };

  const handleAddWaypoint = () => {
    if (directionsServiceOptions.waypoints.length < 5) {
      const newWaypoint = {
        id: uuidv4(),
        value: '',
      };

      setDirectionsServiceOptions({
        ...directionsServiceOptions,
        waypoints: [...directionsServiceOptions.waypoints, newWaypoint],
      });
    } else {
      toast({
        title: t('map.errors.maxWaypointsTitle'),
        description: t('map.errors.maxWaypointsDesc'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteWaypoint = waypointId => {
    const updatedWaypoints = directionsServiceOptions.waypoints.filter(
      waypoint => waypoint.id !== waypointId
    );

    setDirectionsServiceOptions({
      ...directionsServiceOptions,
      waypoints: updatedWaypoints,
    });
  };
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Card
      className="route-form-card"
      position={['absolute', null, null, null, 'absolute']}
      zIndex={!isVisible ? -1 : 6}
      bgColor={'#fff'}
      p={3}
      m={[0, 0, 0, 0, 2]}
      w={['100%', null, null, null, '350px']}
      maxW={['100%', null, null, null, '350px']}
      bottom={0}
      minH={['auto', null, null, null, 'auto']}
      maxH={[
        hasWarningBanner ? 'calc(85dvh - 40px)' : '85dvh',
        null,
        null,
        null,
        '90dvh',
      ]}
      overflowY={'auto'}
      borderRadius={10}
      sx={[
        { paddingBottom: 'env(safe-area-inset-bottom)' },
        null,
        null,
        null,
        { top: [3, null, null, null, 3], left: [3, null, null, null, 3] },
      ]}
      display={'flex'}
      flexDirection={['column', null, null, null, 'column']}
      justifyContent={['flex-start', null, null, null, 'space-evenly']}
    >
      <Center>
        <Box
          mb={2}
          w={'30px'}
          h={'5px'}
          bgColor={'#aeaeae'}
          borderRadius={10}
          alignSelf={'flex-start'}
        ></Box>
      </Center>
      <HStack justify="center" spacing={2} mb={1}>
        <Route size={16} color="#38A169" />
        <Text fontWeight="semibold" fontSize="md" textAlign="center" color="#242424">
          {t('map.form.routeInfo')}
        </Text>
      </HStack>
      <form onSubmit={directionsCallback}>
        <VStack>
          <FormControl>
            <FormLabel mb={0}>{t('map.form.origin')}</FormLabel>

            <Box display="flex" alignItems="center" mt={0} mb={3} gap={2}>
              <LocationAutocomplete
                placeholder={t('map.form.searchOrigin')}
                name="origin"
                value={directionsServiceOptions.origin}
                onChange={onFormInputTextChange}
                onSelect={suggestion => {
                  setAlreadyCalculated(false);
                  setDirectionsServiceOptions({
                    ...directionsServiceOptions,
                    origin: suggestion.displayText,
                  });
                  if (onLocationSelect && suggestion.location) {
                    onLocationSelect(suggestion.location, 'origin');
                  }
                }}
                leftIcon={<ArrowForwardIcon />}
              />
              <IconButton
                backgroundColor={'#fafafa'}
                icon={<AddIcon />}
                onClick={handleAddWaypoint}
                flexShrink={0}
              />
            </Box>

            {directionsServiceOptions.waypoints.length > 0 && (
              <FormLabel>{t('map.form.waypoints')}</FormLabel>
            )}
            {directionsServiceOptions.waypoints.map(waypoint => {
              return (
                <Box
                  key={waypoint.id}
                  display="flex"
                  mt={3}
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-around"
                  width="100%"
                  gap={2}
                >
                  <IconButton
                    icon={<DeleteIcon />}
                    onClick={() => handleDeleteWaypoint(waypoint.id)}
                    flexShrink={0}
                  />
                  <LocationAutocomplete
                    placeholder={t('map.form.searchWaypoint')}
                    name={`waypoint-${waypoint.id}`}
                    value={waypoint.value}
                    onChange={e => onFormWaypointTextChange(e, waypoint.id)}
                    onSelect={suggestion => {
                      setAlreadyCalculated(false);
                      const updatedWaypoints =
                        directionsServiceOptions.waypoints.map(wp =>
                          wp.id === waypoint.id
                            ? { ...wp, value: suggestion.displayText }
                            : wp
                        );
                      setDirectionsServiceOptions({
                        ...directionsServiceOptions,
                        waypoints: updatedWaypoints,
                      });
                    }}
                    leftIcon={<ArrowForwardIcon />}
                  />
                </Box>
              );
            })}
          </FormControl>
          <FormControl>
            <FormLabel mb={0}>{t('map.form.destination')}</FormLabel>
            <LocationAutocomplete
              placeholder={t('map.form.searchDestination')}
              name="destination"
              value={directionsServiceOptions.destination}
              onChange={onFormInputTextChange}
              onSelect={suggestion => {
                setAlreadyCalculated(false);
                setDirectionsServiceOptions({
                  ...directionsServiceOptions,
                  destination: suggestion.displayText,
                });
                if (onLocationSelect && suggestion.location) {
                  onLocationSelect(suggestion.location, 'destination');
                }
              }}
              leftIcon={<ArrowBackIcon />}
            />
          </FormControl>
          <FormControl as="fieldset">
            <FormLabel as="legend">{t('map.form.transport')}</FormLabel>
            <RadioGroup
              defaultValue={TravelMode.DRIVING}
              name="travelMode"
              onChange={onTravelModeChange}
            >
              <HStack spacing={3} flexWrap="wrap">
                <Radio value={TravelMode.DRIVING}>
                  <HStack spacing={1}>
                    <Car size={14} />
                    <Text fontSize="sm">{t('map.form.car')}</Text>
                  </HStack>
                </Radio>
                <Radio value={TravelMode.BICYCLING}>
                  <HStack spacing={1}>
                    <Bike size={14} />
                    <Text fontSize="sm">{t('map.form.bicycle')}</Text>
                  </HStack>
                </Radio>
                <Radio value={TravelMode.WALKING}>
                  <HStack spacing={1}>
                    <Footprints size={14} />
                    <Text fontSize="sm">{t('map.form.walking')}</Text>
                  </HStack>
                </Radio>
              </HStack>
            </RadioGroup>
            <FormHelperText>{t('map.form.transportHelper')}</FormHelperText>
          </FormControl>
          <Box
            display={'flex'}
            w={'100%'}
            flexDir={'column'}
            alignItems={'flex-start'}
          >
            <HStack
              as="button"
              type="button"
              onClick={toggleDropdown}
              cursor="pointer"
              spacing={1}
              align="center"
              color="gray.600"
              _hover={{ color: 'gray.800' }}
              mb={1}
            >
              {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              <Text fontSize="sm" fontWeight="medium">
                {t('map.form.advancedOptions')}
              </Text>
            </HStack>
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ overflow: 'hidden', width: '100%' }}
                >
                  <VStack
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    border="1px solid #e2e8f0"
                    p="3"
                    borderRadius="lg"
                    w="100%"
                    bg="gray.50"
                    spacing={3}
                  >
                    <FormControl>
                      <FormLabel fontSize="sm" mb={1}>
                        {t('map.form.departureTime')}
                      </FormLabel>
                      <Input
                        type="datetime-local"
                        size="sm"
                        borderRadius="md"
                        bg="white"
                        value={toDateTimeLocalValue(departureTime)}
                        min={toDateTimeLocalValue(startOfToday())}
                        max={toDateTimeLocalValue(
                          new Date(
                            Date.now() +
                              FORECAST_WINDOW_DAYS * 24 * 60 * 60 * 1000
                          )
                        )}
                        onChange={e => {
                          const value = e.target.value;
                          if (!setDepartureTime) return;
                          if (!value) {
                            setDepartureTime(new Date());
                            return;
                          }
                          // Clamp to the allowed [today, now + window] range in
                          // case a value is typed outside the picker bounds.
                          const picked = new Date(value);
                          const minDate = startOfToday();
                          const maxDate = new Date(
                            Date.now() +
                              FORECAST_WINDOW_DAYS * 24 * 60 * 60 * 1000
                          );
                          if (picked < minDate) setDepartureTime(minDate);
                          else if (picked > maxDate) setDepartureTime(maxDate);
                          else setDepartureTime(picked);
                        }}
                      />
                      <FormHelperText fontSize="xs">
                        {t('map.form.departureTimeHelper')}
                      </FormHelperText>
                    </FormControl>
                    <Checkbox
                      isChecked={selectedOptions.includes('avoidFerries')}
                      onChange={() => handleOptionChange('avoidFerries')}
                      size="sm"
                    >
                      <Text fontSize="sm">{t('map.form.avoidFerries')}</Text>
                    </Checkbox>
                    <Checkbox
                      isChecked={selectedOptions.includes('avoidHighways')}
                      onChange={() => handleOptionChange('avoidHighways')}
                      size="sm"
                    >
                      <Text fontSize="sm">{t('map.form.avoidHighways')}</Text>
                    </Checkbox>
                    <Checkbox
                      isChecked={selectedOptions.includes('avoidTolls')}
                      onChange={() => handleOptionChange('avoidTolls')}
                      size="sm"
                    >
                      <Text fontSize="sm">{t('map.form.avoidTolls')}</Text>
                    </Checkbox>
                  </VStack>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>

          {errorMessage && (
            <Text color="red.400" fontSize="sm" textAlign="center">
              {errorMessage}
            </Text>
          )}
          <Button
            colorScheme="green"
            onClick={handleSubmit}
            isDisabled={alreadyCalculated}
            w="100%"
            h="48px"
            mt={2}
            borderRadius="xl"
            leftIcon={<Navigation size={16} />}
            fontWeight="medium"
          >
            {t('map.form.calculate')}
          </Button>
        </VStack>
      </form>
    </Card>
  );
}
